import React, { useState, useEffect } from "react";
import { getAdvancedFilters } from "../api/games";
import SearchableSelect from "./ui/SearchableSelect";
import TagMultiSelect from "./ui/TagMultiSelect";
import Button from "./ui/Button";
import GlassPanel from "./ui/GlassPanel";
import { RELEASE_WINDOW_OPTIONS } from "../utils/releaseWindow";

const selectClass =
  "w-full h-9 rounded-lg px-3 text-sm text-white bg-white/[0.04] border border-white/10 outline-none transition-colors duration-150 focus:border-brand-purple-light/70 focus:bg-white/[0.07] appearance-none cursor-pointer";

const FilterField = ({ label, children }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 transition-colors duration-200 hover:border-brand-purple/40">
    <p className="text-brand-gold text-[12px] font-bold uppercase tracking-wide mb-1.5">{label}</p>
    {children}
  </div>
);

const SearchComponent = ({ onSearch }) => {
  const [searchQuery] = useState("");
  // Start with Advanced Search minimized by default
  const [advancedExpanded] = useState(true);
  const [filters, setFilters] = useState({
    genre: "",
    minScore: "",
    platform: "", // Single platform picked from the grouped dropdown
    tags: [], // Changed to array for multiple selection
    releaseWindow: "", // How recent the game is (see releaseWindow util)
    gameMode: "", // New filter for game mode
  });

  // State for filter options loaded from API
  const [filterOptions, setFilterOptions] = useState({
    genres: [],
    platforms: {
      all: [],
      modern: [],
      legacy: [],
      retro: [],
    },
    tags: [],
  });
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Toast notification state
  const [alert, setAlert] = useState({
    show: false,
    type: "info",
    message: "",
  });

  // Load filter options from backend
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingFilters(true);
        const data = await getAdvancedFilters();
        if (data) {
          setFilterOptions({
            genres: data.genres || [],
            platforms: data.platforms || {
              all: [],
              modern: [],
              legacy: [],
              retro: [],
            },
            tags: data.tags?.all || [],
          });
        }
      } catch (error) {
        console.error("Error loading filter options:", error);
        // Fallback data
        setFilterOptions({
          genres: [
            "Action",
            "Adventure",
            "RPG",
            "Strategy",
            "Sports",
            "Racing",
            "Shooter",
            "Puzzle",
          ],
          platforms: {
            all: ["PC", "PlayStation", "Xbox", "Nintendo Switch"],
            modern: [
              "PC",
              "PlayStation 5",
              "PlayStation 4",
              "Xbox Series X/S",
              "Xbox One",
              "Nintendo Switch",
            ],
            legacy: ["PlayStation 3", "Xbox 360", "Nintendo Wii"],
            retro: ["PlayStation 2", "Nintendo 64", "SNES"],
          },
          tags: [
            "Singleplayer",
            "Multiplayer",
            "Co-op",
            "Story Rich",
            "Open World",
          ],
        });
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilters();
  }, []);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(
      () => setAlert({ show: false, type: "info", message: "" }),
      5000,
    );
  };

  const clearAllFilters = () => {
    // Only clear filter fields, NOT the search query
    setFilters({
      genre: "",
      minScore: "",
      platform: "",
      tags: [],
      releaseWindow: "",
      gameMode: "",
    });
    // Don't automatically trigger search - user must click Search button
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.genre) count++;
    if (filters.minScore) count++;
    if (filters.platform) count++;
    if (filters.tags && filters.tags.length > 0) count++;
    if (filters.releaseWindow) count++;
    if (filters.gameMode) count++;
    // Don't count search query as a filter for "Clear All Filters" button
    return count;
  };

  // Build the grouped platform dropdown: the most common platforms first,
  // then a separator, then older / legacy platforms. De-duplicated so a
  // platform never appears in both groups.
  const getPlatformGroups = () => {
    const p = filterOptions.platforms || {};
    const clean = (list) =>
      Array.from(new Set((list || []).filter(Boolean).map((x) => String(x))));

    const popular = clean(p.modern);
    const popularSet = new Set(popular.map((x) => x.toLowerCase()));
    const older = clean([...(p.legacy || []), ...(p.retro || [])]).filter(
      (x) => !popularSet.has(x.toLowerCase()),
    );

    return [
      { label: "Popular platforms", options: popular },
      { label: "Older & legacy platforms", options: older },
    ].filter((group) => group.options.length > 0);
  };

  const runSearch = () => {
    if (!onSearch) return;
    const searchParams = {
      query: searchQuery,
      genre: filters.genre,
      minScore: filters.minScore,
      platform: filters.platform,
      tags: filters.tags,
      releaseWindow: filters.releaseWindow,
      gameMode: filters.gameMode,
    };

    const hasQuery = searchParams.query && searchParams.query.trim().length > 0;
    const hasFilters =
      searchParams.genre ||
      searchParams.minScore ||
      searchParams.platform ||
      searchParams.tags.length > 0 ||
      searchParams.releaseWindow ||
      searchParams.gameMode;

    if (!hasQuery && !hasFilters) {
      showAlert(
        "warning",
        "Please enter a search query or select at least one filter before searching.",
      );
      return;
    }

    onSearch(searchParams);
  };

  const platformGroups = getPlatformGroups();

  return (
    <>
      {/* Toast */}
      {alert.show && (
        <div className="fixed top-[110px] right-4 z-[70] max-w-sm animate-fadeIn">
          <div
            className={[
              "rounded-xl px-4 py-3 shadow-card border text-sm font-medium flex items-start justify-between gap-3",
              alert.type === "warning"
                ? "bg-brand-gold/15 border-brand-gold text-brand-gold"
                : "bg-brand-purple/20 border-brand-purple text-white",
            ].join(" ")}
          >
            <span>{alert.message}</span>
            <button
              onClick={() => setAlert({ show: false, type: "info", message: "" })}
              className="opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center mt-0 mb-1">
        <Button
          variant="gold"
          size="md"
          className="w-full sm:w-auto min-w-[180px]"
          onClick={() => onSearch && onSearch({ quickFilter: "Surprise me" })}
        >
          🎲 Surprise Me
        </Button>
        <p className="mt-2 text-xs text-white/50">Not sure what to play? Let the goblin pick.</p>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-0">
        <GlassPanel className="gg-neon-ring mt-4 sm:mt-6 md:mt-8 mb-4 sm:mb-6 md:mb-8 p-5 sm:p-8 md:p-10 font-body">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mb-5">
            <h3 className="text-white font-extrabold text-xl sm:text-2xl tracking-wide font-heading flex items-center gap-2.5">
              <span className="text-brand-gold">⚙</span> Advanced Search
              {getActiveFiltersCount() > 0 && (
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-brand-gold/20 text-brand-gold border border-brand-gold/50">
                  {getActiveFiltersCount()} active
                </span>
              )}
            </h3>

            {advancedExpanded && (
              <Button
                variant="danger"
                size="sm"
                onClick={clearAllFilters}
                disabled={getActiveFiltersCount() === 0}
              >
                ✕ Clear All Filters
              </Button>
            )}
          </div>

          {advancedExpanded && (
            <div className="animate-fadeIn">
              {/* PRIMARY FILTERS */}
              <div className="mb-5">
                <p className="text-white font-bold text-base mb-3 pl-3 border-l-2 border-brand-gold/80">Primary Filters</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  <FilterField label="Genre">
                    <SearchableSelect
                      options={filterOptions.genres}
                      value={filters.genre}
                      loading={loadingFilters}
                      placeholder="Search or select genre..."
                      onChange={(v) => handleFilterChange("genre", v)}
                    />
                  </FilterField>

                  <FilterField label="Quality Rating">
                    <select
                      className={selectClass}
                      value={filters.minScore}
                      onChange={(e) => handleFilterChange("minScore", e.target.value)}
                    >
                      <option value="">Any score</option>
                      <option value="90">90+ Excellent</option>
                      <option value="80">80+ Very Good</option>
                      <option value="70">70+ Good</option>
                    </select>
                  </FilterField>

                  <FilterField label="Game Mode">
                    <select
                      className={selectClass}
                      value={filters.gameMode}
                      onChange={(e) => handleFilterChange("gameMode", e.target.value)}
                    >
                      <option value="">Any mode</option>
                      <option value="singleplayer">Single Player</option>
                      <option value="multiplayer">Multiplayer</option>
                      <option value="coop">Co-op</option>
                      <option value="competitive">Competitive</option>
                      <option value="online">Online</option>
                      <option value="local">Local</option>
                    </select>
                  </FilterField>
                </div>
              </div>

              {/* PLATFORM */}
              <div className="mb-5">
                <p className="text-white font-bold text-base mb-3 pl-3 border-l-2 border-brand-gold/80">Platform Selection</p>
                <FilterField label="Platform">
                  <select
                    className={selectClass}
                    value={filters.platform}
                    disabled={loadingFilters}
                    onChange={(e) => handleFilterChange("platform", e.target.value)}
                  >
                    <option value="">Any platform</option>
                    {platformGroups.map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((platform) => (
                          <option key={platform} value={platform}>
                            {platform}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </FilterField>
              </div>

              {/* ADDITIONAL FILTERS */}
              <div className="mb-5">
                <p className="text-white font-bold text-base mb-3 pl-3 border-l-2 border-brand-gold/80">Additional Filters</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <FilterField label="Release Date">
                    <select
                      className={selectClass}
                      value={filters.releaseWindow}
                      onChange={(e) => handleFilterChange("releaseWindow", e.target.value)}
                    >
                      {RELEASE_WINDOW_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </FilterField>

                  <FilterField label="Game Tags">
                    <TagMultiSelect
                      options={filterOptions.tags}
                      value={filters.tags}
                      loading={loadingFilters}
                      placeholder="Search or select tags..."
                      onChange={(v) => handleFilterChange("tags", v)}
                    />
                  </FilterField>
                </div>
              </div>

              {/* Search button */}
              <div className="flex justify-center items-center mb-2 mt-2">
                <Button variant="primary" size="lg" onClick={runSearch}>
                  SEARCH
                </Button>
              </div>
            </div>
          )}
        </GlassPanel>
      </div>
    </>
  );
};

export default SearchComponent;
